import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {TabPanel, TabView} from "primereact/tabview";
import {Tag} from "primereact/tag";
import {hasAdminPermission} from "../../../system/admin-access";
import {CONTENT_TYPE_UIDS} from "../../../system/content-type-registry";
import {createFreeWorkspaceSubscription} from "../../../system/support-access-api";
import {useContentTypeData} from "../../../system/use-content-type-data";
import {useTranslate} from "../../../system/useTranslate";
import SubscriptionAdjustDialog from "../components/subscription-adjust-dialog";
import MarkdownHelp from "../../../system/components/markdown-help";
import BillingCollectionTable, {dateColumn, relationColumn, statusColumn} from "../components/billing-collection-table";
import {DataState, RTL_DATATABLE_PROPS, SalesSectionHeader, TableCard} from "../components/common";
import {entityKey, formatNumber, statusSeverity} from "../sales-utils";

function WorkspacesWithoutSubscription() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const workspaces = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE);
    const subscriptions = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION);
    const [creating, setCreating] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            workspaces.ensure({sort: "createdAt:DESC"}, {all: true}),
            subscriptions.ensure({sort: "createdAt:DESC"}, {all: true}),
        ]).catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const subscribed = useMemo(() => new Set(
        subscriptions.items
            .filter((item) => ["draft", "trialing", "active", "past_due", "paused", "suspended"].includes(item.subscriptionStatus))
            .map((item) => String(entityKey(item.workspace)))
    ), [subscriptions.items]);
    const missing = workspaces.items.filter((workspace) => !subscribed.has(String(entityKey(workspace))));
    const canCreate = hasAdminPermission(user, "subscriptions.create");

    const createFree = async (workspace) => {
        setCreating(entityKey(workspace));
        setError(null);
        try {
            await createFreeWorkspaceSubscription(entityKey(workspace));
            await subscriptions.reload({sort: "createdAt:DESC"}, {all: true});
        } catch (requestError) {
            const message = requestError?.response?.data?.error?.message || requestError?.message;
            setError(translate(message));
        } finally {
            setCreating(null);
        }
    };

    return (
        <div className="flex flex-column gap-3">
            <SalesSectionHeader title={translate("billing-admin-workspaces-without-subscription")} description={translate("billing-admin-workspaces-without-subscription-description")}/>
            <TableCard>
                <DataState loading={workspaces.loading || subscriptions.loading} error={error || workspaces.error || subscriptions.error} empty={!missing.length}>
                    <DataTable {...RTL_DATATABLE_PROPS} value={missing} paginator rows={10}>
                        <Column className="text-right" field="name" header={translate("sales-workspace")}/>
                        <Column className="text-right" field="workspaceStatus" header={translate("sales-status")} body={(row) => <Tag severity={statusSeverity(row.workspaceStatus)} value={row.workspaceStatus}/>}/>
                        <Column className="text-right" header={translate("actions")} body={(row) => (
                            <Button className="text-xs gap-2" icon="pi pi-plus" label={translate("billing-admin-create-free-subscription")} disabled={!canCreate} loading={String(creating) === String(entityKey(row))} onClick={() => createFree(row)}/>
                        )}/>
                    </DataTable>
                </DataState>
            </TableCard>
            <MarkdownHelp contentKey="billing-admin-without-subscription-help"/>
        </div>
    );
}

export default function SubscriptionsDomainTab() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const canView = hasAdminPermission(user, "subscriptions.view");
    const canCreate = hasAdminPermission(user, "subscriptions.create");
    const canOverride = hasAdminPermission(user, "subscriptions.override");
    const canViewUsage = hasAdminPermission(user, "subscriptions.usage.view");
    const canAdjust = hasAdminPermission(user, "subscriptions.change") || canOverride;
    const [adjusting, setAdjusting] = useState(null);
    return (
        <>
        <SubscriptionAdjustDialog documentId={adjusting?.documentId} visible={Boolean(adjusting)} onHide={() => setAdjusting(null)} onSaved={() => adjusting?.reload?.()}/>
        <TabView className="rtl text-xs">
            {canView && <TabPanel header={translate("billing-admin-subscriptions")}>
                <BillingCollectionTable
                    uid={CONTENT_TYPE_UIDS.SUBSCRIPTION}
                    titleKey="billing-admin-subscriptions"
                    columns={[
                        relationColumn("workspace", "sales-workspace"),
                        relationColumn("plan", "billing-admin-plan"),
                        statusColumn("subscriptionStatus"),
                        statusColumn("billingStatus", "billing-admin-billing-status"),
                        dateColumn("currentPeriodEnd", "billing-admin-period-end"),
                    ]}
                    rowAction={canAdjust ? (row, reload) => (
                        <Button type="button" outlined className="text-xs gap-2" icon="pi pi-sliders-h" label={translate("billing-admin-adjust-button")}
                                onClick={() => setAdjusting({documentId: row.documentId, reload})}/>
                    ) : null}
                />
            </TabPanel>}
            {canCreate && <TabPanel header={translate("billing-admin-without-subscription")}><WorkspacesWithoutSubscription/></TabPanel>}
            {canOverride && <TabPanel header={translate("billing-admin-overrides")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.SUBSCRIPTION_ENTITLEMENT_OVERRIDE} titleKey="billing-admin-overrides" columns={[
                    relationColumn("subscription", "billing-admin-subscription"),
                    relationColumn("feature", "billing-admin-feature"),
                    {field: "operation", headerKey: "billing-admin-operation"},
                    statusColumn("subscriptionEntitlementOverrideStatus"),
                    dateColumn("endsAt", "billing-admin-valid-until"),
                ]}/>
            </TabPanel>}
            {canViewUsage && <TabPanel header={translate("billing-admin-usage")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.SUBSCRIPTION_USAGE} titleKey="billing-admin-usage" columns={[
                    relationColumn("workspace", "sales-workspace"),
                    relationColumn("feature", "billing-admin-feature"),
                    {field: "used", headerKey: "billing-admin-used", body: (row) => formatNumber(row.used)},
                    {field: "reserved", headerKey: "billing-admin-reserved", body: (row) => formatNumber(row.reserved)},
                    dateColumn("periodEnd", "billing-admin-period-end"),
                ]}/>
            </TabPanel>}
            {canOverride && <TabPanel header={translate("billing-admin-addons")}>
                <BillingCollectionTable uid={CONTENT_TYPE_UIDS.SUBSCRIPTION_ADDON} titleKey="billing-admin-addons" columns={[
                    relationColumn("subscription", "billing-admin-subscription"),
                    {field: "name", headerKey: "name"},
                    {field: "quantity", headerKey: "billing-admin-quantity"},
                    statusColumn("subscriptionAddonStatus"),
                ]}/>
            </TabPanel>}
        </TabView>
        </>
    );
}
